/**
 * SUUNSU Skin Advisor
 * Pure theme implementation — no app, no server.
 *
 * Data flow:
 *   1. sections/skin-advisor-quiz.liquid dumps every product that has an
 *      `advisor.*` metafield set into a <script type="application/json"> tag.
 *   2. This file runs the question flow entirely client-side, scores the
 *      dumped product list against the answers, and renders a 7-step routine.
 *   3. Selected products are added to the cart in one call via /cart/add.js.
 *   4. Because the Storefront API only allows *reading* metafields (not
 *      writing them), quiz results are persisted in localStorage and as
 *      cart attributes (visible on the order in Shopify admin) instead of a
 *      native customer metafield. See README for the upgrade path.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'suunsu_skin_advisor_result_v1';

  var STEP_DEFS = [
    { n: 1, label: 'Cleanser' },
    { n: 2, label: 'Toner' },
    { n: 3, label: 'Essence / Serum' },
    { n: 4, label: 'Moisturizer' },
    { n: 5, label: 'Eye Care' },
    { n: 6, label: 'Sun Care' },
    { n: 7, label: 'Weekly Mask' }
  ];

  var CONCERN_LABELS = {
    'Pores': 'pores and texture',
    'Acne/Blemishes': 'breakouts',
    'Pigmentation': 'uneven tone and dark spots',
    'Dullness': 'dullness',
    'Wrinkles': 'fine lines and firmness',
    'Barrier damage': 'a compromised barrier',
    'Redness': 'redness',
    'Dehydration': 'dehydration'
  };

  var QUESTIONS = [
    {
      id: 'skin_type',
      type: 'single',
      title: 'After cleansing, wait 30–60 minutes with nothing applied. How does your skin feel?',
      hint: 'Bare skin, no products on it — this is the most reliable way to read your real skin type.',
      options: [
        { value: 'Dry', label: 'Tight, flaky or rough', desc: 'Feels uncomfortable, maybe visible dry patches' },
        { value: 'Combination', label: 'Shiny only in the T-zone', desc: 'Forehead / nose / chin look oily, cheeks stay normal or dry' },
        { value: 'Oily', label: 'Shiny all over', desc: 'Visible shine across the whole face' },
        { value: 'Sensitive', label: 'Red, itchy or stinging', desc: 'Reacts before you have even applied anything' },
        { value: 'Normal', label: 'Comfortable, no real change', desc: 'Balanced, nothing in particular stands out' }
      ]
    },
    {
      id: 'reactivity',
      type: 'single',
      title: 'How often does your skin react when you try a new product?',
      options: [
        { value: 'high', label: 'Often — I have to be careful', desc: 'Redness, stinging or breakouts are common' },
        { value: 'medium', label: 'Sometimes, with certain ingredients', desc: 'Occasional reaction to fragrance or strong actives' },
        { value: 'low', label: 'Rarely or never', desc: 'My skin tolerates most things well' }
      ]
    },
    {
      id: 'concerns',
      type: 'multi',
      max: 2,
      title: 'What are your top skin concerns right now?',
      hint: 'Choose up to 2 — we will prioritise these when picking products.',
      options: [
        { value: 'Pores', label: 'Enlarged pores / blackheads' },
        { value: 'Acne/Blemishes', label: 'Breakouts or blemishes' },
        { value: 'Pigmentation', label: 'Dark spots / uneven tone' },
        { value: 'Dullness', label: 'Dull, tired-looking skin' },
        { value: 'Wrinkles', label: 'Fine lines / loss of firmness' },
        { value: 'Barrier damage', label: 'Compromised, reactive barrier' },
        { value: 'Redness', label: 'Redness or flushing' },
        { value: 'Dehydration', label: 'Tightness / dehydration' }
      ]
    },
    {
      id: 'actives',
      type: 'single',
      title: 'How experienced are you with active ingredients like retinol, AHA or BHA?',
      options: [
        { value: 'none', label: 'Never used them', desc: "Let's start gently" },
        { value: 'occasional', label: 'I use gentle ones occasionally' },
        { value: 'tolerant', label: 'I use them regularly and my skin tolerates it well' }
      ]
    },
    {
      id: 'texture',
      type: 'single',
      title: 'What texture do you enjoy wearing, especially for moisturizer and sunscreen?',
      options: [
        { value: 'light', label: 'Light, watery or gel' },
        { value: 'balanced', label: 'Balanced lotion' },
        { value: 'rich', label: 'Rich cream' },
        { value: 'none', label: 'No preference' }
      ]
    },
    {
      id: 'values',
      type: 'multi',
      max: 2,
      exclusive: 'none',
      title: 'Any ingredient preferences we should filter for?',
      options: [
        { value: 'Vegan', label: 'Vegan', desc: 'Skips snail mucin, collagen and other animal-derived ingredients' },
        { value: 'Fragrance Free', label: 'Fragrance-free only' },
        { value: 'none', label: 'No preference' }
      ]
    },
    {
      id: 'depth',
      type: 'single',
      title: 'How many steps do you want in your daily routine?',
      options: [
        { value: 'essential', label: 'Just the essentials', desc: 'Cleanser, moisturizer, sunscreen' },
        { value: 'standard', label: 'A solid daily routine', desc: 'Adds toner and a treatment serum' },
        { value: 'full', label: 'The full K-beauty experience', desc: 'All 7 steps, including eye care and a weekly mask' }
      ]
    },
    {
      id: 'age',
      type: 'single',
      title: 'Which age range are you in?',
      hint: 'Helps us judge how much to prioritise firming and prevention.',
      options: [
        { value: 'under25', label: 'Under 25' },
        { value: '25-34', label: '25–34' },
        { value: '35-44', label: '35–44' },
        { value: '45plus', label: '45+' }
      ]
    }
  ];

  // ---------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------
  var ALL_PRODUCTS = [];
  var answers = {};
  var qIndex = 0;
  var recommendations = {};   // { 1: [product, product, product], ... }
  var selection = {};         // { 1: { productId, on }, ... }

  var root, introEl, appEl, progressEl, progressFill, progressLabel, qStage, resultsStage, cartbar;

  // ---------------------------------------------------------------------
  // Boot
  // ---------------------------------------------------------------------
  document.addEventListener('DOMContentLoaded', init);

  function init() {
    root = document.getElementById('skin-advisor');
    if (!root) return;

    var dataEl = document.getElementById('sa-product-data');
    try {
      ALL_PRODUCTS = JSON.parse(dataEl.textContent || '[]');
    } catch (e) {
      ALL_PRODUCTS = [];
    }
    ALL_PRODUCTS.forEach(normalizeProduct);

    introEl = document.getElementById('sa-intro');
    appEl = document.getElementById('sa-app');
    progressEl = document.getElementById('sa-progress');
    progressFill = document.getElementById('sa-progress-fill');
    progressLabel = document.getElementById('sa-progress-label');
    qStage = document.getElementById('sa-question-stage');
    resultsStage = document.getElementById('sa-results-stage');
    cartbar = document.getElementById('sa-cartbar');

    document.getElementById('sa-start-btn').addEventListener('click', function () { startQuiz(false); });
    var resumeBtn = document.getElementById('sa-resume-btn');
    document.getElementById('sa-add-to-cart-btn').addEventListener('click', addSelectedToCart);

    var saved = loadSaved();
    if (saved) {
      resumeBtn.hidden = false;
      resumeBtn.addEventListener('click', function () {
        answers = saved.answers;
        startQuiz(true);
      });
    }
  }

  function normalizeProduct(p) {
    p.skin_type = p.skin_type || [];
    p.skin_concern = p.skin_concern || [];
    p.certification = p.certification || [];
    p.key_ingredient = p.key_ingredient || [];
    p.texture = p.texture || '';
    p.advisor_note = p.advisor_note || '';
    p.step_number = parseInt((p.routine_step || '').match(/^(\d+)/), 10) || 0;
  }

  // ---------------------------------------------------------------------
  // Quiz flow
  // ---------------------------------------------------------------------
  function startQuiz(skipToResults) {
    introEl.hidden = true;
    appEl.hidden = false;
    if (skipToResults) {
      finishQuiz(true);
    } else {
      answers = {};
      qIndex = 0;
      progressEl.hidden = false;
      renderQuestion();
    }
    root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function renderQuestion() {
    resultsStage.hidden = true;
    qStage.hidden = false;
    progressEl.hidden = false;
    root.scrollIntoView({ behavior: 'smooth', block: 'start' });

    var q = QUESTIONS[qIndex];
    progressFill.style.width = Math.round(((qIndex) / QUESTIONS.length) * 100) + '%';
    progressLabel.textContent = 'Question ' + (qIndex + 1) + ' of ' + QUESTIONS.length;

    var current = answers[q.id];
    var isMulti = q.type === 'multi';
    if (isMulti && !Array.isArray(current)) current = answers[q.id] = [];

    var html = '';
    html += '<div class="sa-question">';
    html += '<h2 class="sa-question__title">' + escapeHtml(q.title) + '</h2>';
    if (q.hint) html += '<p class="sa-question__hint">' + escapeHtml(q.hint) + '</p>';
    if (isMulti) html += '<p class="sa-question__count-hint" id="sa-count-hint"></p>';
    html += '<div class="sa-options" role="group">';
    q.options.forEach(function (opt) {
      var pressed = isMulti ? current.indexOf(opt.value) !== -1 : current === opt.value;
      html += '<button type="button" class="sa-option" data-type="' + (isMulti ? 'checkbox' : 'radio') + '" ' +
        'data-value="' + escapeHtml(opt.value) + '" aria-pressed="' + pressed + '">' +
        '<span class="sa-option__mark"></span>' +
        '<span><span class="sa-option__label">' + escapeHtml(opt.label) + '</span>' +
        (opt.desc ? '<span class="sa-option__desc">' + escapeHtml(opt.desc) + '</span>' : '') +
        '</span></button>';
    });
    html += '</div>';
    html += '<div class="sa-question__nav">';
    html += qIndex > 0 ? '<button type="button" class="sa-btn sa-btn--ghost" id="sa-back-btn">Back</button>' : '<span></span>';
    html += '<button type="button" class="sa-btn sa-btn--primary" id="sa-next-btn">' + (qIndex === QUESTIONS.length - 1 ? 'See my routine' : 'Next') + '</button>';
    html += '</div></div>';

    qStage.innerHTML = html;

    updateCountHint(q);
    updateNextState(q);

    var optionButtons = qStage.querySelectorAll('.sa-option');
    optionButtons.forEach(function (btn) {
      btn.addEventListener('click', function () { onOptionClick(q, btn); });
    });
    var backBtn = document.getElementById('sa-back-btn');
    if (backBtn) backBtn.addEventListener('click', goBack);
    document.getElementById('sa-next-btn').addEventListener('click', goNext);
  }

  function onOptionClick(q, btn) {
    var value = btn.getAttribute('data-value');
    if (q.type === 'single') {
      answers[q.id] = value;
      qStage.querySelectorAll('.sa-option').forEach(function (b) {
        b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
      });
    } else {
      var list = answers[q.id] || (answers[q.id] = []);
      var idx = list.indexOf(value);

      if (q.exclusive) {
        if (value === q.exclusive) {
          answers[q.id] = idx === -1 ? [q.exclusive] : [];
        } else {
          var excIdx = list.indexOf(q.exclusive);
          if (excIdx !== -1) list.splice(excIdx, 1);
          toggleInList(list, value, q.max);
        }
      } else {
        toggleInList(list, value, q.max);
      }
      qStage.querySelectorAll('.sa-option').forEach(function (b) {
        var v = b.getAttribute('data-value');
        b.setAttribute('aria-pressed', (answers[q.id] || []).indexOf(v) !== -1 ? 'true' : 'false');
      });
    }
    updateCountHint(q);
    updateNextState(q);
  }

  function toggleInList(list, value, max) {
    var idx = list.indexOf(value);
    if (idx !== -1) {
      list.splice(idx, 1);
    } else if (!max || list.length < max) {
      list.push(value);
    }
  }

  function updateCountHint(q) {
    var hint = document.getElementById('sa-count-hint');
    if (!hint || q.type !== 'multi') return;
    var count = (answers[q.id] || []).length;
    hint.textContent = q.max ? (count + ' of ' + q.max + ' selected') : (count + ' selected');
  }

  function updateNextState(q) {
    var nextBtn = document.getElementById('sa-next-btn');
    if (!nextBtn) return;
    var valid;
    if (q.type === 'single') {
      valid = !!answers[q.id];
    } else {
      valid = (answers[q.id] || []).length > 0;
    }
    nextBtn.disabled = !valid;
  }

  function goBack() {
    if (qIndex === 0) return;
    qIndex--;
    renderQuestion();
  }

  function goNext() {
    if (qIndex < QUESTIONS.length - 1) {
      qIndex++;
      renderQuestion();
    } else {
      finishQuiz(false);
    }
  }

  // ---------------------------------------------------------------------
  // Scoring
  // ---------------------------------------------------------------------
  function scoreProduct(p, a) {
    var derivedTypes = [a.skin_type];
    if (a.reactivity === 'high' && derivedTypes.indexOf('Sensitive') === -1) derivedTypes.push('Sensitive');
    if ((a.concerns || []).indexOf('Acne/Blemishes') !== -1) derivedTypes.push('Acne prone');

    var score = 0;
    var pType = p.skin_type;
    var pConcern = p.skin_concern;
    var pCert = p.certification;
    var ingredientText = ((p.key_ingredient || []).join(' ') + ' ' + (p.title || '')).toLowerCase();

    if (pType.indexOf('All skin types') !== -1) score += 2;
    derivedTypes.forEach(function (t) {
      if (t && t !== 'Normal' && pType.indexOf(t) !== -1) score += 3;
    });

    (a.concerns || []).forEach(function (c) {
      if (pConcern.indexOf(c) !== -1) score += 3;
    });

    if (a.reactivity === 'high') {
      if (pConcern.indexOf('Redness') !== -1 || pConcern.indexOf('Barrier damage') !== -1) score += 1;
      if (pType.indexOf('Sensitive') !== -1) score += 2;
    }

    var hasStrongActive = /retinol|retinal|glycolic|salicylic|\baha\b|\bbha\b|\bpha\b/.test(ingredientText);
    if (hasStrongActive) {
      if (a.actives === 'none') score -= 5;
      else if (a.actives === 'occasional') score -= 1;
      else if (a.actives === 'tolerant') score += 2;
      if (a.reactivity === 'high') score -= 3;
    } else if (a.actives === 'none') {
      score += 1;
    }

    if (a.texture && a.texture !== 'none' && p.texture) {
      var tex = p.texture.toLowerCase();
      var lightTex = ['gel', 'essence', 'water', 'mist'];
      var richTex = ['cream', 'balm'];
      var balancedTex = ['lotion', 'serum'];
      if (a.texture === 'light' && lightTex.some(function (t) { return tex.indexOf(t) !== -1; })) score += 1;
      if (a.texture === 'rich' && richTex.some(function (t) { return tex.indexOf(t) !== -1; })) score += 1;
      if (a.texture === 'balanced' && balancedTex.some(function (t) { return tex.indexOf(t) !== -1; })) score += 1;
    }

    var values = a.values || [];
    if (values.indexOf('Vegan') !== -1) {
      if (pCert.indexOf('Vegan') !== -1) score += 2;
      else if (/snail|collagen|exosome|milk/.test(ingredientText)) score -= 3;
    }
    if (values.indexOf('Fragrance Free') !== -1) {
      if (pCert.some(function (c) { return /fragrance free/i.test(c); })) score += 2;
    }


    return score;
  }

  function buildRecommendations(a) {
    var recs = {};
    STEP_DEFS.forEach(function (step) {
      var candidates = ALL_PRODUCTS.filter(function (p) { return p.step_number === step.n && p.available; });
      var scored = candidates.map(function (p) { return { p: p, s: scoreProduct(p, a) }; });
      scored.sort(function (x, y) {
        if (y.s !== x.s) return y.s - x.s;
        return (x.p.price || 0) - (y.p.price || 0);
      });
      recs[step.n] = scored.slice(0, 3).map(function (x) { return x.p; });
    });
    return recs;
  }

  function stepDefaultOn(n, a) {
    if (n === 1 || n === 4 || n === 6) return true;
    if (n === 2 || n === 3) return a.depth !== 'essential';
    if (n === 5) {
      return a.depth === 'full' || a.age === '35-44' || a.age === '45plus' ||
        (a.concerns || []).indexOf('Wrinkles') !== -1 || (a.concerns || []).indexOf('Dullness') !== -1;
    }
    if (n === 7) {
      return a.depth === 'full' || (a.concerns || []).indexOf('Acne/Blemishes') !== -1 || (a.concerns || []).indexOf('Redness') !== -1;
    }
    return true;
  }

  // ---------------------------------------------------------------------
  // Results
  // ---------------------------------------------------------------------
  function finishQuiz(fromSaved) {
    recommendations = buildRecommendations(answers);
    selection = {};
    STEP_DEFS.forEach(function (step) {
      var list = recommendations[step.n];
      if (list && list.length) {
        selection[step.n] = { productId: list[0].id, on: stepDefaultOn(step.n, answers) };
      } else {
        selection[step.n] = null;
      }
    });

    if (!fromSaved) persistResult();

    progressEl.hidden = true;
    qStage.hidden = true;
    resultsStage.hidden = false;
    renderResults();
    cartbar.hidden = false;
    root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function buildAdvisorSummary(a) {
    var typeText = {
      Dry: 'a dry skin type that needs consistent barrier support and richer textures to stop that tight, flaky feeling',
      Oily: 'an oily skin type that produces more sebum than it needs — the goal is balance, not stripping it dry',
      Combination: 'a combination skin type: oilier through the T-zone while the rest of your face stays calmer, so it helps to layer lighter products first',
      Sensitive: 'a reactive, easily-irritated skin type that does best with a short list of calming, fragrance-conscious ingredients',
      Normal: 'a balanced skin type — the priority is maintaining that balance rather than fixing anything urgent'
    };
    var out = 'You have ' + (typeText[a.skin_type] || 'a skin type worth watching closely') + '.';

    if (a.reactivity === 'high') {
      out += ' Because your skin reacts easily, we steered away from strong exfoliating acids and high-percentage actives, and leaned toward soothing, shorter-ingredient formulas.';
    } else if (a.reactivity === 'medium') {
      out += ' Since certain ingredients occasionally bother you, we kept active ingredients at a moderate strength — introduce anything new one at a time.';
    }

    if (a.concerns && a.concerns.length) {
      var list = a.concerns.map(function (c) { return CONCERN_LABELS[c] || c; }).join(' and ');
      out += ' Your main focus right now is ' + list + ', so the picks below are weighted toward ingredients that address that directly.';
    }

    if (a.actives === 'none') {
      out += ' Since you are new to active ingredients, we kept this routine gentle and left stronger treatments like retinol out for now — you can layer those in later once your skin has built a baseline tolerance.';
    } else if (a.actives === 'tolerant' && a.concerns && (a.concerns.indexOf('Wrinkles') !== -1 || a.concerns.indexOf('Pigmentation') !== -1)) {
      out += ' Because you already tolerate actives well, we included a stronger treatment step to match your goals.';
    }

    var depthText = { essential: 'a lean, essentials-only routine', standard: 'a complete daily routine', full: 'the full 7-step routine' };
    out += ' Below is ' + (depthText[a.depth] || 'your routine') + ', laid out in the order to apply each product. Every step is optional — add or remove anything before you check out.';

    return out;
  }

  function renderResults() {
    var a = answers;
    var html = '';
    html += '<div class="sa-results">';
    html += '<div class="sa-results__header"><span class="sa-results__badge">Your result</span>';
    html += '<h2 class="sa-results__title">Your personal routine</h2></div>';
    html += '<div class="sa-advisor-note">' + escapeHtml(buildAdvisorSummary(a)).replace(/\n/g, '<br>') + '</div>';

    var allOn = STEP_DEFS.every(function (step) { return !selection[step.n] || selection[step.n].on; });
    html += '<div class="sa-results__toolbar">';
    html += '<label class="sa-toggle-all"><input type="checkbox" id="sa-toggle-all"' + (allOn ? ' checked' : '') + '> Select entire routine</label>';
    html += '<button type="button" class="sa-retake" id="sa-retake-btn">Retake the quiz</button>';
    html += '</div>';

    STEP_DEFS.forEach(function (step) {
      var sel = selection[step.n];
      var list = recommendations[step.n] || [];
      html += '<div class="sa-step" data-step="' + step.n + '">';
      html += '<div class="sa-step__head">';
      html += '<input type="checkbox" class="sa-step-checkbox" data-step="' + step.n + '" ' + (sel && sel.on ? 'checked' : '') + (sel ? '' : ' disabled') + '>';
      html += '<span class="sa-step__num">' + step.n + '</span>';
      html += '<span class="sa-step__name">' + escapeHtml(step.label) + '</span>';
      if (!stepDefaultOn(step.n, a)) html += '<span class="sa-step__optional-tag">Optional</span>';
      html += '</div>';

      if (!sel) {
        html += '<div class="sa-step__empty">No matching product yet for this step — check back soon.</div>';
        html += '</div>';
        return;
      }

      var product = list.filter(function (p) { return p.id === sel.productId; })[0] || list[0];
      html += renderStepBody(step, product, list, sel.on);
      html += '</div>';
    });

    html += '</div>';
    resultsStage.innerHTML = html;
    wireResultsEvents();
    updateCartBar();
  }

  function renderStepBody(step, product, list, on) {
    var html = '<div class="sa-step__body' + (on ? '' : ' is-off') + '">';
    html += '<img class="sa-step__img" src="' + escapeAttr(product.image || '') + '" alt="' + escapeAttr(product.title) + '" loading="lazy">';
    html += '<div>';
    html += '<p class="sa-step__product-vendor">' + escapeHtml(product.vendor || '') + '</p>';
    html += '<p class="sa-step__product-title"><a href="' + escapeAttr(product.url) + '" target="_blank" rel="noopener">' + escapeHtml(product.title) + '</a></p>';
    html += '<p class="sa-step__product-price">' + escapeHtml(product.price_formatted || '') + (product.available ? '' : ' · Currently unavailable') + '</p>';
    if (product.advisor_note) html += '<p class="sa-step__note">' + escapeHtml(truncate(product.advisor_note, 190)) + '</p>';

    var tags = [].concat(product.skin_concern || [], product.certification || []).slice(0, 4);
    if (tags.length) {
      html += '<div class="sa-step__tags">' + tags.map(function (t) { return '<span class="sa-tag">' + escapeHtml(t) + '</span>'; }).join('') + '</div>';
    }

    html += '<div class="sa-step__actions">';
    if (list.length > 1) {
      html += '<button type="button" class="sa-swap-toggle" data-step="' + step.n + '">Choose a different product (' + (list.length - 1) + ' more)</button>';
    }
    html += '</div>';

    if (list.length > 1) {
      html += '<div class="sa-alternates" hidden data-alt-for="' + step.n + '">';
      list.forEach(function (alt) {
        html += '<button type="button" class="sa-alt" data-step="' + step.n + '" data-product-id="' + alt.id + '" aria-pressed="' + (alt.id === product.id) + '">';
        html += '<img src="' + escapeAttr(alt.image || '') + '" alt="">';
        html += '<span><strong>' + escapeHtml(alt.title) + '</strong><span>' + escapeHtml(alt.price_formatted || '') + '</span></span>';
        html += '</button>';
      });
      html += '</div>';
    }

    html += '</div></div>';
    return html;
  }

  function wireResultsEvents() {
    document.getElementById('sa-retake-btn').addEventListener('click', function () {
      qIndex = 0;
      qStage.hidden = false;
      resultsStage.hidden = true;
      cartbar.hidden = true;
      renderQuestion();
    });

    document.getElementById('sa-toggle-all').addEventListener('change', function (e) {
      var on = e.target.checked;
      STEP_DEFS.forEach(function (step) {
        if (selection[step.n]) selection[step.n].on = on;
      });
      renderResults();
    });

    resultsStage.querySelectorAll('.sa-step-checkbox').forEach(function (cb) {
      cb.addEventListener('change', function () {
        var n = parseInt(cb.getAttribute('data-step'), 10);
        selection[n].on = cb.checked;
        renderResults();
      });
    });

    resultsStage.querySelectorAll('.sa-swap-toggle').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var n = btn.getAttribute('data-step');
        var panel = resultsStage.querySelector('[data-alt-for="' + n + '"]');
        if (panel) panel.hidden = !panel.hidden;
      });
    });

    resultsStage.querySelectorAll('.sa-alt').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var n = parseInt(btn.getAttribute('data-step'), 10);
        var pid = parseInt(btn.getAttribute('data-product-id'), 10);
        selection[n].productId = pid;
        selection[n].on = true;
        renderResults();
      });
    });
  }

  function updateCartBar() {
    var count = 0, total = 0;
    STEP_DEFS.forEach(function (step) {
      var sel = selection[step.n];
      if (sel && sel.on) {
        var product = (recommendations[step.n] || []).filter(function (p) { return p.id === sel.productId; })[0];
        if (product && product.available) {
          count++;
          total += product.price || 0;
        }
      }
    });
    document.getElementById('sa-cartbar-count').textContent = count + (count === 1 ? ' item selected' : ' items selected');
    document.getElementById('sa-cartbar-total').textContent = count ? formatMoney(total) : '';
    document.getElementById('sa-add-to-cart-btn').disabled = count === 0;
  }

  // ---------------------------------------------------------------------
  // Cart
  // ---------------------------------------------------------------------
  function addSelectedToCart() {
    var items = [];
    STEP_DEFS.forEach(function (step) {
      var sel = selection[step.n];
      if (sel && sel.on) {
        var product = (recommendations[step.n] || []).filter(function (p) { return p.id === sel.productId; })[0];
        if (product && product.available && product.variant_id) {
          items.push({ id: product.variant_id, quantity: 1, properties: { 'Recommended by': 'Skin Advisor · Step ' + step.n + ' ' + step.label } });
        }
      }
    });

    if (!items.length) return;

    var btn = document.getElementById('sa-add-to-cart-btn');
    btn.disabled = true;
    var originalLabel = btn.textContent;
    btn.textContent = 'Adding…';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (res) { if (!res.ok) throw new Error('cart add failed'); return res.json(); })
      .then(function () { return attachCartAttributes(); })
      .then(function () { window.location.href = '/cart'; })
      .catch(function () {
        btn.disabled = false;
        btn.textContent = originalLabel;
        alert('Something went wrong adding these products to your cart. Please try again.');
      });
  }

  function attachCartAttributes() {
    var attrs = {
      'Skin Advisor - Skin Type': answers.skin_type || '',
      'Skin Advisor - Concerns': (answers.concerns || []).join(', '),
      'Skin Advisor - Routine Depth': answers.depth || '',
      'Skin Advisor - Completed At': new Date().toISOString()
    };
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    }).catch(function () { /* non-fatal if this fails */ });
  }

  // ---------------------------------------------------------------------
  // Persistence (localStorage — see README for the customer-metafield upgrade path)
  // ---------------------------------------------------------------------
  function persistResult() {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
        answers: answers,
        savedAt: new Date().toISOString()
      }));
    } catch (e) { /* storage unavailable — ignore */ }
  }

  function loadSaved() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  // ---------------------------------------------------------------------
  // Utilities
  // ---------------------------------------------------------------------
  function formatMoney(cents) {
    var amount = (cents || 0) / 100;
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: currency }).format(amount);
    } catch (e) {
      return currency + ' ' + amount.toFixed(2);
    }
  }

  function truncate(str, len) {
    if (!str || str.length <= len) return str;
    return str.slice(0, len).replace(/\s+\S*$/, '') + '…';
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function escapeAttr(str) { return escapeHtml(str); }

})();